import * as React from "react"

import { Badge, Skeleton } from "@ibada/ui"

import { useTranslations } from "@/i18n"
import { useFinanceFeatureQuery } from "@/features/finance/services/finance-query"
import { useReconciliationSessionsQuery } from "@/features/finance/services/finance-close-trust-query"
import { ReconciliationSessionStatus } from "@/features/finance/model/finance-close-trust-types"
import { FinanceCategoryScope } from "@/features/finance/model/finance-types"
import { useWidgetMenu } from "@ibada/dashboard-engine"

import { WidgetBoundary } from "@/features/finance/overview/components/widget-boundary"
import { ProgressBar } from "@/features/finance/overview/components/progress-bar"
import { useFinanceOverviewScope } from "@/features/finance/overview/context/finance-overview-context"
import { formatPercent } from "@/features/finance/overview/lib/overview-format"
import { OverviewRoutes, useOverviewNav } from "@/features/finance/overview/lib/overview-nav"

interface ReconRow {
  id: string
  name: string
  reconciled: boolean
}

interface ReconciliationData {
  totalAccounts: number
  reconciledCount: number
  needingCount: number
  openSessions: number
  ratioPercent: number
  rows: ReconRow[]
  hasAccounts: boolean
}

/**
 * Reconciliation Status — CURRENT-STATE (period-independent). There is no backend rollup endpoint yet, so
 * this widget derives the summary on the client from the accounts list + the reconciliation-sessions list:
 * an account counts as reconciled when it has a BALANCED or CLOSED session. Total eligible accounts, the
 * reconciled ratio, open (DRAFT) sessions, and a short account list are all computed here.
 * DEFERRED to backend: a dedicated rollup (incl. reliable "oldest unreconciled date").
 */
export function ReconciliationStatusWidget() {
  const t = useTranslations("finance.overview")
  const scope = useFinanceOverviewScope()
  const go = useOverviewNav()
  const { useFinanceAccountsQuery } = useFinanceFeatureQuery()

  const accountsQuery = useFinanceAccountsQuery(
    scope.workspaceId
      ? {
          workspaceId: scope.workspaceId,
          branchId: scope.branchId,
          // System (parish) branch accounts are served under PARISH scope; BRANCH scope on it is rejected.
          scope: scope.context === "parish" ? FinanceCategoryScope.Parish : FinanceCategoryScope.Branch,
          limit: 100,
        }
      : null,
  )
  const sessionsQuery = useReconciliationSessionsQuery(
    scope.workspaceId ? { workspaceId: scope.workspaceId, branchId: scope.branchId } : null,
  )

  const data = React.useMemo<ReconciliationData | undefined>(() => {
    if (!accountsQuery.data || !sessionsQuery.data) return undefined
    const accounts = accountsQuery.data.items
    const sessions = sessionsQuery.data.items

    // Only an APPROVED session proves an account (IBA-161). A submitted-but-undecided one is a claim, and a
    // zero difference nobody has signed off is not evidence — counting either would overstate assurance.
    const reconciledIds = new Set(
      sessions
        .filter((session) => session.status === ReconciliationSessionStatus.Approved)
        .map((session) => session.financeAccount.id),
    )
    // Work still in flight: being prepared, or waiting on a reviewer.
    const openSessions = sessions.filter(
      (session) =>
        session.status === ReconciliationSessionStatus.Draft ||
        session.status === ReconciliationSessionStatus.PendingReview,
    ).length

    const totalAccounts = accounts.length
    const reconciledCount = accounts.filter((account) => reconciledIds.has(account.id)).length
    const needingCount = Math.max(0, totalAccounts - reconciledCount)
    const ratioPercent = totalAccounts > 0 ? (reconciledCount / totalAccounts) * 100 : 0

    const rows = accounts
      .map((account) => ({ id: account.id, name: account.name, reconciled: reconciledIds.has(account.id) }))
      .sort((a, b) => Number(a.reconciled) - Number(b.reconciled)) // surface pending accounts first
      .slice(0, 3)

    return { totalAccounts, reconciledCount, needingCount, openSessions, ratioPercent, rows, hasAccounts: totalAccounts > 0 }
  }, [accountsQuery.data, sessionsQuery.data])

  const refresh = React.useCallback(() => {
    void accountsQuery.refetch()
    void sessionsQuery.refetch()
  }, [accountsQuery, sessionsQuery])

  useWidgetMenu({ refresh, viewDetails: go.canNavigateTo(OverviewRoutes.reconciliation) ? () => go(OverviewRoutes.reconciliation) : undefined })

  return (
    <WidgetBoundary
      isLoading={accountsQuery.isLoading || sessionsQuery.isLoading}
      isError={accountsQuery.isError || sessionsQuery.isError}
      isFetching={accountsQuery.isFetching || sessionsQuery.isFetching}
      data={data}
      isEmpty={(d) => !d.hasAccounts}
      skeleton={<ReconciliationSkeleton />}
      errorTitle={t("widgets.reconciliationStatus.error")}
      onRetry={refresh}
      retryLabel={t("widgets.common.retry")}
      emptyTitle={t("widgets.reconciliationStatus.empty.title")}
    >
      {(d) => (
        <div className="space-y-3">
          <div className="flex items-end justify-between gap-2">
            <p className="text-2xl font-semibold tabular-nums tracking-tight text-foreground">
              {formatPercent(d.ratioPercent)}
            </p>
            <p className="text-xs text-muted-foreground">
              {t("widgets.reconciliationStatus.ratio", { reconciled: d.reconciledCount, total: d.totalAccounts })}
            </p>
          </div>

          <ProgressBar
            value={d.ratioPercent}
            tone={d.ratioPercent >= 80 ? "success" : d.ratioPercent > 0 ? "primary" : "muted"}
            aria-label={t("widgets.reconciliationStatus.title")}
          />

          {d.openSessions > 0 ? (
            <p className="text-xs text-muted-foreground">
              {t("widgets.reconciliationStatus.openSessions", { count: d.openSessions })}
            </p>
          ) : null}

          <ul className="divide-y divide-border/60">
            {d.rows.map((row) => (
              <li key={row.id} className="flex items-center justify-between gap-2 py-1.5">
                <span className="min-w-0 truncate text-sm text-foreground">{row.name}</span>
                <Badge variant={row.reconciled ? "success" : "secondary"} className="shrink-0">
                  {row.reconciled
                    ? t("widgets.reconciliationStatus.status.reconciled")
                    : t("widgets.reconciliationStatus.status.pending")}
                </Badge>
              </li>
            ))}
          </ul>

          {scope.canEdit ? (
            <button
              type="button"
              onClick={() => go(OverviewRoutes.reconciliation)}
              className="text-xs font-medium text-primary hover:underline"
            >
              {t("widgets.reconciliationStatus.start")}
            </button>
          ) : null}
        </div>
      )}
    </WidgetBoundary>
  )
}

function ReconciliationSkeleton() {
  return (
    <div className="space-y-3">
      <div className="flex items-end justify-between">
        <Skeleton className="h-7 w-16" />
        <Skeleton className="h-3 w-24" />
      </div>
      <Skeleton className="h-1.5 w-full" />
      {[0, 1, 2].map((row) => (
        <div key={row} className="flex items-center justify-between py-1">
          <Skeleton className="h-3 w-28" />
          <Skeleton className="h-4 w-16 rounded-full" />
        </div>
      ))}
    </div>
  )
}
